import { useQuery } from "@tanstack/react-query";

import {
  DEFAULT_ONBOARDING_VARIANT_ID,
  getOnboardingVariantFlagKey,
  ONBOARDING_VARIANT_TIMEOUT_MS,
  OnboardingEntryPointType,
  OnboardingVariantIdType,
  resolveOnboardingVariantId,
} from "@/domain/onboarding";
import mixpanel from "@/services/mixpanel";

export const OnboardingQueryKeys = {
  all: ["onboarding"] as const,
  variant: (code: string) =>
    [...OnboardingQueryKeys.all, "variant", code] as const,
};

const fetchOnboardingVariantId = async ({
  entryPointType,
  workspaceId,
}: {
  entryPointType: OnboardingEntryPointType;
  workspaceId: string;
}): Promise<OnboardingVariantIdType> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const hasLoadedFlags = await Promise.race([
    mixpanel
      .updateFlagsContext({ entryPointType, workspaceId })
      .then(() => true),
    new Promise<boolean>(resolve => {
      timeoutId = setTimeout(
        () => resolve(false),
        ONBOARDING_VARIANT_TIMEOUT_MS,
      );
    }),
  ]).finally(() => clearTimeout(timeoutId));

  // On timeout the user gets the default variant, so the flag isn't read: that
  // would log an exposure to a variant they never see.
  if (!hasLoadedFlags) {
    return DEFAULT_ONBOARDING_VARIANT_ID;
  }

  const variantValue = await mixpanel.getVariantValue({
    flagKey: getOnboardingVariantFlagKey(entryPointType),
    fallbackValue: DEFAULT_ONBOARDING_VARIANT_ID,
  });

  return resolveOnboardingVariantId(variantValue);
};

export const useOnboardingVariantQuery = ({
  code,
  entryPointType,
  workspaceId,
  isEnabled,
}: {
  code: string;
  entryPointType: OnboardingEntryPointType | undefined;
  workspaceId: string | undefined;
  isEnabled: boolean;
}) =>
  useQuery({
    queryKey: OnboardingQueryKeys.variant(code),
    queryFn: () =>
      entryPointType && workspaceId
        ? fetchOnboardingVariantId({ entryPointType, workspaceId })
        : DEFAULT_ONBOARDING_VARIANT_ID,
    enabled: isEnabled && Boolean(entryPointType && workspaceId),
    staleTime: Infinity,
    retry: false,
  });
